/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiBadge from "components/VuiBadge";

// Icons
import { IoArrowUp, IoArrowDown, IoList } from "react-icons/io5";

function RecentTransactions({ transactions, loading }) {
  const formatDate = (timestamp) => {
    if (!timestamp || isNaN(timestamp)) return 'Unknown';
    const date = new Date(timestamp * 1000);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleString('de-DE', {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return Math.abs(amount).toFixed(4);
  };

  const getTypeInfo = (category) => {
    switch(category) {
      case 'send':
        return { 
          icon: <IoArrowUp size="16px" color="#FF0080" />, 
          text: 'Send',
          color: 'error'
        };
      case 'receive':
        return { 
          icon: <IoArrowDown size="16px" color="#00CED1" />, 
          text: 'Receive',
          color: 'success'
        };
      case 'generate':
        return { 
          icon: <IoArrowDown size="16px" color="#FFD700" />, 
          text: 'Mining',
          color: 'warning'
        };
      default:
        return { 
          icon: <IoArrowDown size="16px" color="#00CED1" />, 
          text: category,
          color: 'info'
        };
    }
  };

  const getStatusColor = (confirmations) => {
    if (confirmations === 0) return 'warning';
    if (confirmations < 6) return 'info';
    return 'success';
  };

  return (
    <Card>
      <VuiBox p={3}>
        <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <VuiBox display="flex" alignItems="center">
            <VuiBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="48px"
              height="48px"
              borderRadius="12px"
              sx={{
                background: "#00CED1",
              }}
            >
              <IoList size="24px" color="#000000" />
            </VuiBox>
            <VuiBox ml={2}>
              <VuiTypography variant="lg" color="white" fontWeight="bold">
                Recent Transactions
              </VuiTypography>
              <VuiTypography variant="button" color="text">
                Last 10 transactions
              </VuiTypography>
            </VuiBox>
          </VuiBox>
          {loading && (
            <Icon sx={{ color: "warning.main" }}>sync</Icon>
          )}
        </VuiBox>

        {loading ? (
          <VuiBox textAlign="center" py={4}>
            <Icon sx={{ color: "text.main", fontSize: "32px", mb: 1 }}>sync</Icon>
            <VuiTypography variant="button" color="text">
              Loading transactions...
            </VuiTypography>
          </VuiBox>
        ) : transactions.length === 0 ? (
          <VuiBox textAlign="center" py={4}>
            <Icon sx={{ color: "text.main", fontSize: "32px", mb: 1 }}>inbox</Icon>
            <VuiTypography variant="button" color="text">
              No transactions yet
            </VuiTypography>
          </VuiBox>
        ) : (
          <VuiBox>
            {transactions.slice(0, 10).map((tx, index) => {
              const typeInfo = getTypeInfo(tx.category);
              
              return (
                <VuiBox
                  key={tx.txid || index}
                  p={2}
                  mb={2}
                  sx={{
                    background: "rgba(255, 255, 255, 0.02)",
                    borderRadius: "8px",
                    border: "1px solid rgba(255, 255, 255, 0.05)",
                    "&:hover": {
                      background: "rgba(255, 255, 255, 0.05)",
                    },
                  }}
                >
                  <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <VuiBox display="flex" alignItems="center">
                      {typeInfo.icon}
                      <VuiTypography pl="8px" variant="button" color={typeInfo.color} fontWeight="medium">
                        {typeInfo.text}
                      </VuiTypography>
                    </VuiBox>
                    <VuiBadge
                      variant="standard"
                      badgeContent={tx.confirmations === 0 ? 'Pending' : tx.confirmations < 6 ? `${tx.confirmations} Conf` : 'Confirmed'}
                      color={getStatusColor(tx.confirmations)}
                      size="xs"
                      container
                    />
                  </VuiBox>
                  
                  <VuiBox display="flex" justifyContent="space-between" alignItems="center">
                    <VuiBox>
                      <VuiTypography variant="button" color="white" fontWeight="bold">
                        {formatAmount(tx.amount)} MYT
                      </VuiTypography>
                      <VuiTypography variant="caption" color="text" display="block">
                        {formatDate(tx.time)}
                      </VuiTypography>
                    </VuiBox>
                    {tx.address && (
                      <VuiTypography variant="caption" color="text" fontFamily="monospace">
                        {tx.address.substring(0, 8)}...
                      </VuiTypography>
                    )}
                  </VuiBox>
                </VuiBox>
              );
            })}
          </VuiBox>
        )}
      </VuiBox>
    </Card>
  );
}

export default RecentTransactions;
